/**
 * RoleGate — wraps the entire SysOps Panel.
 *
 * Allowed roles: `SystemOps` OR `SuperAdmin`. Anything else gets the
 * "no access" landing.
 *
 * No bypass-key escape — assignment must come through the v3 Admin panel
 * (Wave 6.1 usersEditor + rolesEditor) which writes to
 * `dbo.UserRoleAssignments`.
 */

import { type ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { AccessDenied } from '@/modules/AccessDenied/AccessDenied';

const ALLOWED_ROLES = ['SystemOps', 'SuperAdmin'] as const;

export function RoleGate({ children }: { children: ReactNode }): JSX.Element {
  const { user, status, loading, error, refresh } = useAuth();

  if (loading) {
    return <div className="loading-screen">Signing in to SysOps Panel…</div>;
  }

  if (status === 'error') {
    return (
      <div className="auth-gate">
        <div className="auth-gate__card">
          <h1 className="auth-gate__title">Sign-in failed</h1>
          <p className="auth-gate__body">
            {error ?? 'Unable to complete Entra sign-in.'}
          </p>
          <button className="btn btn--primary" onClick={() => void refresh()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return <div className="loading-screen">Resolving identity…</div>;
  }

  const hasAllowedRole = user.roles.some((r) =>
    (ALLOWED_ROLES as readonly string[]).includes(r),
  );

  if (!hasAllowedRole) {
    return <AccessDenied user={user} />;
  }

  return <>{children}</>;
}
